/**
 * Attendance V2 — correction request modal.
 * Supports fixing ANY wrong time in one request: clock-in, any break
 * segment (edit/add/remove), and clock-out. Mandatory >=20-char reason.
 * Creates ONE pending manual_edits[] entry; owner approves/rejects it whole.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Trash2, Plus } from 'lucide-react';
import { attendanceV2API } from '../../lib/api';
import { StellarDateTimePicker } from '../StellarDateTimePicker';
import { fmtTime, fmtDateLong } from '../../lib/attendanceV2';

const REASON_MIN = 20;

export default function CorrectionModal({ open, onOpenChange, record, isOwner, onSubmitted }) {
  const [fixIn, setFixIn] = useState(false);
  const [fixOut, setFixOut] = useState(false);
  const [fixBreaks, setFixBreaks] = useState(false);
  const [inVal, setInVal] = useState('');
  const [outVal, setOutVal] = useState('');
  const [breakRows, setBreakRows] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && record) {
      setFixIn(false);
      setFixOut(!record.clock_out_time); // most common case: forgot to clock out
      setFixBreaks(false);
      setInVal(record.clock_in_time || '');
      setOutVal(record.clock_out_time || '');
      setBreakRows((record.breaks || []).map((b) => ({
        id: b.id,
        start: b.break_start_time || '',
        end: b.break_end_time || '',
      })));
      setReason('');
    }
  }, [open, record]);

  if (!record) return null;

  const submit = async () => {
    const proposed = {};
    if (fixIn) {
      if (!inVal) { toast.error('Pick a valid clock-in time'); return; }
      proposed.clock_in_time = inVal;
    }
    if (fixOut) {
      if (!outVal) { toast.error('Pick a valid clock-out time'); return; }
      proposed.clock_out_time = outVal;
    }
    if (fixBreaks) {
      const breaks = [];
      for (let i = 0; i < breakRows.length; i++) {
        const r = breakRows[i];
        if (!r.start || !r.end) { toast.error(`Break #${i + 1} needs both start and end times`); return; }
        breaks.push({ id: r.id || undefined, break_start_time: r.start, break_end_time: r.end });
      }
      proposed.breaks = breaks;
    }
    if (!Object.keys(proposed).length) {
      toast.error('Pick at least one thing to correct'); return;
    }
    if (reason.trim().length < REASON_MIN) {
      toast.error(`Please explain the correction (at least ${REASON_MIN} characters)`); return;
    }
    setSubmitting(true);
    try {
      const res = await attendanceV2API.requestCorrection(record.id, {
        proposed_changes: proposed, reason_text: reason.trim(),
      });
      if (isOwner) {
        // Owner corrections apply immediately (request + self-approve).
        await attendanceV2API.approveCorrection(record.id, res.data.edit.id, 'Owner direct correction');
        toast.success('Correction applied');
      } else {
        toast.success('Correction submitted — waiting for owner approval');
      }
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit the correction, please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const row = 'flex items-center gap-2';
  const remaining = Math.max(0, REASON_MIN - reason.trim().length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="av2-correction-modal">
        <DialogHeader>
          <DialogTitle>Fix attendance — {fmtDateLong(record.date)}</DialogTitle>
          <DialogDescription>
            Recorded: in {fmtTime(record.clock_in_time)} · out {fmtTime(record.clock_out_time)}.
            {isOwner ? ' As owner, your correction applies immediately.' : ' Your request goes to the owner for approval.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <label className={row}>
              <Checkbox checked={fixIn} onCheckedChange={(v) => setFixIn(!!v)} data-testid="av2-fix-in-toggle" />
              <span className="text-[13px] font-medium">Correct clock-in time</span>
            </label>
            {fixIn && (
              <StellarDateTimePicker value={inVal} onChange={setInVal}
                placeholder="Pick clock-in date & time" testId="av2-fix-in-input" />
            )}
          </div>
          <div className="space-y-2">
            <label className={row}>
              <Checkbox checked={fixOut} onCheckedChange={(v) => setFixOut(!!v)} data-testid="av2-fix-out-toggle" />
              <span className="text-[13px] font-medium">Correct clock-out time</span>
            </label>
            {fixOut && (
              <StellarDateTimePicker value={outVal} onChange={setOutVal}
                placeholder="Pick clock-out date & time" testId="av2-fix-out-input" />
            )}
          </div>
          <div className="space-y-2">
            <label className={row}>
              <Checkbox checked={fixBreaks} onCheckedChange={(v) => setFixBreaks(!!v)} data-testid="av2-fix-breaks-toggle" />
              <span className="text-[13px] font-medium">Correct breaks</span>
              <span className="text-[11px] text-muted-foreground">(replaces all break times for this day)</span>
            </label>
            {fixBreaks && (
              <div className="space-y-2">
                {breakRows.map((r, i) => (
                  <div key={r.id || i} className="flex items-center gap-1.5">
                    <div className="flex-1 min-w-0">
                      <StellarDateTimePicker value={r.start}
                        onChange={(v) => setBreakRows((p) => p.map((x, j) => j === i ? { ...x, start: v } : x))}
                        placeholder="Break start" testId={`av2-break-start-${i}`} />
                    </div>
                    <span className="text-muted-foreground text-[11px]">→</span>
                    <div className="flex-1 min-w-0">
                      <StellarDateTimePicker value={r.end}
                        onChange={(v) => setBreakRows((p) => p.map((x, j) => j === i ? { ...x, end: v } : x))}
                        placeholder="Break end" testId={`av2-break-end-${i}`} />
                    </div>
                    <button type="button" onClick={() => setBreakRows((p) => p.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-red-400" data-testid={`av2-break-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px]"
                  onClick={() => setBreakRows((p) => [...p, { start: '', end: '' }])} data-testid="av2-break-add">
                  <Plus className="h-3 w-3 mr-1" /> Add missing break
                </Button>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <span className="text-[13px] font-medium">Reason / explanation *</span>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Explain what happened (e.g., forgot to clock out after the client call ended at 6 PM)…"
              rows={3} className="text-[13px]" data-testid="av2-reason-input" />
            <span className={`text-[11px] ${remaining > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
              {remaining > 0 ? `${remaining} more characters required` : `${reason.trim().length} characters`}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="av2-correction-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || reason.trim().length < REASON_MIN}
            data-testid="av2-correction-submit">
            {submitting ? 'Submitting…' : isOwner ? 'Apply correction' : 'Submit for approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
